import { z } from 'zod';

/**
 * The administration wire contract (comparaison finale spec §7, C10; auth
 * Tahiti spec §5).
 *
 * One place that names what the two sides of the wire have to agree on for the
 * administration screens: the header the shared token travels in, the refusal
 * codes the login screen has to word in French, and the view of a session.
 * They live here rather than in the backend because BOTH sides read them, and
 * a name spelt twice is a name that will one day be spelt differently.
 *
 * A HEADER and never the query string: the two access words of the public
 * links are an anti-robot latch that leaks into browser history, this one is a
 * secret (sessions testeurs spec §15).
 */
export const ADMIN_TOKEN_HEADER = 'x-admin-token';

/**
 * Why the administration door refused (auth Tahiti §5).
 *
 * A closed enumeration because the screen turns each one into a French
 * sentence through a `Record`: a code added here without its wording stops
 * compiling, which is the régime of `lib/model-labels.ts`. The server
 * `message` never reaches an operator's eyes.
 *
 * The four are not interchangeable, and the two that matter most are the last
 * two: `too-many-attempts` read as a wrong password sends a legitimate
 * operator retyping a correct one faster and faster, and `identity-unavailable`
 * read as a wrong password sends them doubting their own account during an
 * outage - which is the screen that has to name the break-glass (T7).
 */
export const adminRefusalCodeSchema = z.enum([
  'invalid-credentials',
  'email-not-verified',
  'too-many-attempts',
  'identity-unavailable',
]);
export type AdminRefusalCode = z.infer<typeof adminRefusalCodeSchema>;

/**
 * Who is connected, and NOTHING else.
 *
 * `.strict()` is not decorative here: it is what keeps a whole Tahiti user
 * object from being handed to the browser one day « because we had it to
 * hand ». The screen needs to name who is connected; it does not need an
 * identity provider's record.
 */
export const adminSessionViewSchema = z.object({ email: z.string().email() }).strict();
export type AdminSessionView = z.infer<typeof adminSessionViewSchema>;

/**
 * Why the door refused, as it travels on the wire.
 *
 * Shaped like `ProtocolRefusal` and for its reason: `details` is `unknown` on
 * the error envelope, so nothing but a typed annotation stops a code the screen
 * has no phrase for from reaching an operator. The `message` inside is an
 * English developer string - it belongs to the logs and to the OpenAPI
 * description, never to an operator's screen (variantes §11.2).
 */
export const adminRefusalSchema = z
  .object({ code: adminRefusalCodeSchema, message: z.string() })
  .strict();
export type AdminRefusal = z.infer<typeof adminRefusalSchema>;

/**
 * Who is connected, IF ANYONE - the answer of `GET /api/admin/session`.
 *
 * A schema of its own rather than a loosening of the one above, because the two
 * answer different questions. That one is « who just logged in », and an email
 * is the only honest answer to it. This one is asked by a shell that may be
 * authenticated by the SHARED TOKEN, which names a machine and not a person:
 * `null` says exactly that, where an invented email would be a lie and an
 * absent field would read as « this backend cannot tell ».
 *
 * It is NOT a way to ask which credential the guard accepted - the guard never
 * says, and a client able to read it would start depending on it (T10). It is
 * the account door reporting whether it has an account.
 */
export const adminSessionStatusSchema = z.object({ email: z.string().email().nullable() }).strict();
export type AdminSessionStatus = z.infer<typeof adminSessionStatusSchema>;

/**
 * What the login form puts on the wire. Declared here because the screen builds
 * it and the route parses it.
 *
 * `.strict()` so a browser cannot smuggle a third field past the one route of
 * this lot whose job is to hand two secrets to a third party. The password is
 * BOUNDED rather than free: unbounded, it is a body of any size accepted at the
 * only open door the lot adds.
 */
export const adminLoginRequestSchema = z
  .object({ email: z.string().email(), password: z.string().min(1).max(512) })
  .strict();
export type AdminLoginRequest = z.infer<typeof adminLoginRequestSchema>;

/**
 * The readable prefix of a personal operator token (§20.2, H36).
 *
 * It lives here rather than in the backend for the reason `ADMIN_TOKEN_HEADER`
 * does: both sides of the wire could read it - a screen that ever lists these
 * tokens has to recognise one, and the minting script prints one - and a
 * prefix spelt twice is a prefix that will one day be spelt differently.
 *
 * A prefix is not decoration. It is what makes a leaked token RECOGNISABLE: a
 * secret scanner, a log scrubber or a human reading a paste can tell what they
 * are looking at, which is exactly what a bare 64 hex characters cannot say.
 * The token itself travels in `Authorization: Bearer`, never in
 * `ADMIN_TOKEN_HEADER`: each key has its own lock, and the guard does not have
 * to guess which one it is being handed.
 */
export const OPERATOR_TOKEN_PREFIX = 'me_pat_';

/**
 * A personal token as its owner sees it - and the SHAPE THAT HAS NO SECRET IN
 * IT (§20.2).
 *
 * `.strict()` is load-bearing here and not a habit: the row in the base
 * carries `token_hash` and `email` beside these fields, and a projection that
 * ever grew a field « because we had it to hand » would put a fingerprint on
 * the wire. The fingerprint is not the token, but it is the only thing an
 * offline attacker needs to confirm a guess, and there is no reason whatsoever
 * for it to leave the base.
 *
 * `lastUsedAt` is nullable because a token that has never been presented has
 * never been used - that is the state that says « this one was minted and
 * forgotten ». `revokedAt` is nullable for the symmetric reason, and a revoked
 * token stays in the list: a row that disappeared would leave nothing to read
 * when somebody asks what happened to a key.
 */
export const operatorTokenSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    createdAt: z.string(),
    lastUsedAt: z.string().nullable(),
    revokedAt: z.string().nullable(),
  })
  .strict();
export type OperatorToken = z.infer<typeof operatorTokenSchema>;

/** « My tokens » - the answer of `GET /api/admin/tokens`, revoked ones included. */
export const operatorTokenListSchema = z.object({ tokens: z.array(operatorTokenSchema) }).strict();
export type OperatorTokenList = z.infer<typeof operatorTokenListSchema>;

/**
 * How long a label may be, named because BOTH sides read it.
 *
 * The route refuses past it in a 400, and the screen that mints (Réglages >
 * Jetons, #84) stops the field there so the refusal never happens. That is the
 * régime of `ADMIN_TOKEN_HEADER` at the head of this file: a bound spelled
 * twice is a bound that will one day be spelled differently, and the half that
 * drifts is always the one nobody is looking at - here the form, which would
 * then let an operator type a label the server throws away.
 */
export const OPERATOR_TOKEN_LABEL_MAX = 60;

/**
 * What minting asks for: a label, and nothing else.
 *
 * `.strict()` so nothing can be smuggled alongside it - in particular neither
 * an `email` nor an `id`. WHOSE token this is comes from the COOKIE and never
 * from the body: a caller who could name the owner could mint somebody else a
 * key, which is the one thing this route must be unable to do.
 *
 * Bounded at `OPERATOR_TOKEN_LABEL_MAX`: the label is read in a list, and an
 * unbounded string is a body of any size accepted on an authenticated write.
 */
export const operatorTokenCreateRequestSchema = z
  .object({ label: z.string().min(1).max(OPERATOR_TOKEN_LABEL_MAX) })
  .strict();
export type OperatorTokenCreateRequest = z.infer<typeof operatorTokenCreateRequestSchema>;

/**
 * The ONE answer that carries the token in clear (§20.2).
 *
 * It is the view plus `token`, and the split is the point: every other read of
 * a token answers `operatorTokenSchema`, which has no secret in it. The clear
 * value exists for the length of one response and is never stored, never
 * logged and never served again - the base keeps only its SHA-256 fingerprint,
 * so a lost token is re-minted rather than recovered.
 */
export const operatorTokenMintedSchema = operatorTokenSchema
  .extend({ token: z.string().min(1) })
  .strict();
export type OperatorTokenMinted = z.infer<typeof operatorTokenMintedSchema>;
